import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from 'react-query';
import { Button, Center, Spinner } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  InformationBanner,
  isRequiredQuestionAnswered,
  type DynamicQuestionAnswerValue,
} from 'src/components/molecules';
import { Modal } from 'src/components/molecules/Modal/Modal';
import {
  CategoryDisplayType,
  useDeleteQuestionnaireResponseMutation,
  useGetOrganisationQuestionnairesQuery,
  useGetQuestionnaireQuestionsQuery,
  useRegisterQuestionnaireResponseAttachmentMutation,
} from 'src/graphql/generated';
import { FormState } from 'src/types';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import {
  Container,
  EmptyStateWrapper,
  InformationBannerContainer,
  SectionDescription,
  SectionHeader,
  SectionHeaderContainer,
  SectionTitle,
  Spacer,
} from './ManualIntakeDynamicQuestions.styles';
import { hydrateFileUploadEvidenceInAnswersState } from './hydrateFileUploadEvidenceInAnswers';
import { mapAnswersToSaveInput } from './mapAnswersToSaveInput';
import {
  QUESTIONNAIRE_QUERY_PARAMS,
  type AnswersState,
  type ManualIntakeDynamicQuestionsPayload,
  type ManualIntakeDynamicQuestionsProps,
  type ManualIntakeDynamicQuestionsRef,
} from './manualIntakeDynamicQuestions.types';
import { ManualIntakeQuestionnaireSelector } from './ManualIntakeQuestionnaireSelector';
import { ManualIntakeQuestionsForm } from './ManualIntakeQuestionsForm';
import { sanitizeQuestionnaireDescription } from './sanitizeQuestionnaireDescription';
import { useManualIntakeDeleteQaAttachmentMutation } from './useManualIntakeDeleteQaAttachmentMutation';

export type {
  ManualIntakeDynamicQuestionsPayload,
  ManualIntakeDynamicQuestionsProps,
  ManualIntakeDynamicQuestionsRef,
  SubmitReportPayload,
} from './manualIntakeDynamicQuestions.types';

export const ManualIntakeDynamicQuestions = forwardRef<
  ManualIntakeDynamicQuestionsRef,
  ManualIntakeDynamicQuestionsProps
>(function ManualIntakeDynamicQuestions(
  {
    initialQuestionnaireId,
    initialResponseId,
    initialAnswers,
    savedResponseId,
    evidenceUploadReportId,
    disclosureCaseInvalidateCaseId,
    isDraftQuestionnaireResponseLoading = false,
    canSelectQuestionnaire = true,
    canSubmitReport = true,
    isSubmittingReport = false,
    currentReport,
    updateReport,
    onSubmitReport,
    onSubmitReportFromReview,
  },
  ref,
) {
  const queryClient = useQueryClient();
  const disclosureCaseInvalidateCaseIdRef = useRef(
    disclosureCaseInvalidateCaseId,
  );
  disclosureCaseInvalidateCaseIdRef.current = disclosureCaseInvalidateCaseId;
  const deleteAttachmentOptsRef = useRef<
    { onError?: (error: unknown) => void } | undefined
  >(undefined);

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | null
  >(initialQuestionnaireId ?? null);
  const [selectedQuestionnaireName, setSelectedQuestionnaireName] = useState<
    string | null
  >(null);
  const [
    selectedQuestionnaireDescription,
    setSelectedQuestionnaireDescription,
  ] = useState<ReactNode>(null);
  const [responseId, setResponseId] = useState<string | null>(
    initialResponseId ?? null,
  );
  const [answers, setAnswers] = useState<AnswersState>(initialAnswers ?? {});
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [showChangeFormModal, setShowChangeFormModal] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (savedResponseId != null) setResponseId(savedResponseId);
  }, [savedResponseId]);

  const { mutateAsync: deleteResponseAsync } =
    useDeleteQuestionnaireResponseMutation();

  const { data: questionnairesData, isLoading: questionnairesLoading } =
    useGetOrganisationQuestionnairesQuery({
      queryParams: QUESTIONNAIRE_QUERY_PARAMS,
    });

  const {
    data: questionsData,
    isLoading: questionsLoading,
    isFetching: questionsFetching,
  } = useGetQuestionnaireQuestionsQuery(
    { questionnaireId: selectedQuestionnaireId ?? '' },
    { enabled: Boolean(selectedQuestionnaireId) },
  );

  const { mutateAsync: registerQaAttachment } =
    useRegisterQuestionnaireResponseAttachmentMutation();

  const deleteQuestionnaireAttachmentMutation =
    useManualIntakeDeleteQaAttachmentMutation({
      answersRef,
      disclosureCaseInvalidateCaseIdRef,
      deleteAttachmentOptsRef,
      setAnswers,
    });

  const questionnaires = useMemo(
    () =>
      (questionnairesData?.organisationQuestionnaires ?? []).filter(
        (q): q is typeof q & { id: string } =>
          Boolean(q?.id) && Boolean(q.categoryId),
      ),
    [questionnairesData?.organisationQuestionnaires],
  );

  const questions = useMemo(
    () =>
      [...(questionsData?.questionnaireQuestions?.questions ?? [])].sort(
        (a, b) => (a?.orderIndex ?? 0) - (b?.orderIndex ?? 0),
      ),
    [questionsData],
  );

  useEffect(() => {
    if (initialQuestionnaireId != null) {
      setSelectedQuestionnaireId(initialQuestionnaireId);
      const selected = questionnaires.find(
        (q) => q.id === initialQuestionnaireId,
      );
      setSelectedQuestionnaireName(
        selected?.publicTitle ?? selected?.internalName ?? null,
      );
      setSelectedQuestionnaireDescription(
        sanitizeQuestionnaireDescription(selected?.description),
      );
    }
  }, [initialQuestionnaireId, questionnaires]);

  useEffect(() => {
    if (initialResponseId != null) setResponseId(initialResponseId);
    setAnswers((prev) => {
      let next = prev;
      if (initialAnswers != null) {
        next = { ...prev, ...initialAnswers };
      }
      if (questions.length > 0) {
        const hydrated = hydrateFileUploadEvidenceInAnswersState(
          next,
          questions,
          { evidenceReportId: evidenceUploadReportId ?? null },
        );
        if (hydrated !== next) {
          next = hydrated;
        }
      }
      return next === prev ? prev : next;
    });
  }, [initialResponseId, initialAnswers, questions, evidenceUploadReportId]);

  const handleRegisterQuestionnaireAttachment = useCallback(
    async (input: { questionId: string; evidenceS3Path: string }) => {
      const rid = responseId ?? savedResponseId;
      const reportId = evidenceUploadReportId;
      if (!rid?.trim() || !reportId?.trim()) {
        return undefined;
      }
      try {
        const res = await registerQaAttachment({
          responseId: rid,
          reportId,
          questionId: input.questionId,
          evidenceS3Path: input.evidenceS3Path,
        });
        const attachmentId =
          res.registerQuestionnaireResponseAttachment?.attachment?.id ??
          undefined;
        if (
          attachmentId &&
          disclosureCaseInvalidateCaseId != null &&
          disclosureCaseInvalidateCaseId.trim() !== ''
        ) {
          void queryClient.invalidateQueries({
            queryKey: [
              'DisclosureCaseById',
              { caseId: disclosureCaseInvalidateCaseId },
            ],
          });
        }
        return attachmentId;
      } catch (error) {
        const errMeta =
          error instanceof Error
            ? { message: error.message, stack: error.stack }
            : {};
        console.error({
          error,
          responseId: rid,
          attachmentId: undefined,
          questionId: input.questionId,
          reportId,
          traceId: undefined,
          requestId: undefined,
          ...errMeta,
        });
        handleErrorToast({
          toastMessage: 'Failed to register file with questionnaire service',
        });
        return undefined;
      }
    },
    [
      responseId,
      savedResponseId,
      evidenceUploadReportId,
      registerQaAttachment,
      queryClient,
      disclosureCaseInvalidateCaseId,
    ],
  );

  const handleDeleteQuestionnaireAttachment = useCallback(
    async (
      input: { attachmentId: string; questionId: string },
      opts?: { onError?: (error: unknown) => void },
    ) => {
      const rid = responseId ?? savedResponseId;
      const reportId = evidenceUploadReportId;
      if (
        !rid?.trim() ||
        !input.attachmentId.trim() ||
        !input.questionId.trim()
      ) {
        return;
      }
      deleteAttachmentOptsRef.current = opts;
      try {
        await deleteQuestionnaireAttachmentMutation.mutateAsync({
          responseId: rid,
          attachmentId: input.attachmentId.trim(),
          reportId: reportId ?? undefined,
          questionId: input.questionId.trim(),
        });
      } catch {
        // Mutation onError handles rollback, logging, and user feedback.
      }
    },
    [
      responseId,
      savedResponseId,
      evidenceUploadReportId,
      deleteQuestionnaireAttachmentMutation,
    ],
  );

  useImperativeHandle(
    ref,
    () => ({
      getDynamicQuestionsPayload(): ManualIntakeDynamicQuestionsPayload | null {
        if (!selectedQuestionnaireId) return null;
        const mapped = mapAnswersToSaveInput(answers);
        return {
          questionnaireId: selectedQuestionnaireId,
          responseId: responseId ?? undefined,
          answers: mapped,
        };
      },
    }),
    [selectedQuestionnaireId, responseId, answers],
  );

  const handleQuestionnaireSelect = useCallback(
    (
      questionnaireId: string,
      questionnaireName: string | null | undefined,
      questionnaireDescription: string | null | undefined,
      questionnaireCategoryId: number | null | undefined,
    ) => {
      if (!canSelectQuestionnaire) return;
      if (questionnaireId === selectedQuestionnaireId && responseId != null) {
        return;
      }
      setSelectedQuestionnaireId(questionnaireId);
      setSelectedQuestionnaireName(questionnaireName ?? null);
      setSelectedQuestionnaireDescription(
        sanitizeQuestionnaireDescription(questionnaireDescription),
      );
      if (currentReport) {
        updateReport({
          ...currentReport,
          reportedCategory: {
            id: questionnaireCategoryId,
            name: null,
          } as unknown as CategoryDisplayType,
        });
      }
      setAnswers({});
      setResponseId(null);
      setSubmitAttempted(false);
    },
    [
      canSelectQuestionnaire,
      selectedQuestionnaireId,
      responseId,
      currentReport,
      updateReport,
    ],
  );

  const setAnswer = useCallback(
    (questionId: string, value: DynamicQuestionAnswerValue) => {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  const allRequiredQuestionsAnswered = useMemo(() => {
    return questions.every((question) => {
      const questionId = question?.id == null ? '' : String(question.id);
      const questionText = question?.questionText?.trim() ?? '';
      const value =
        answers[questionId] ??
        (questionText ? answers[`questionText:${questionText}`] : undefined) ??
        {};
      return isRequiredQuestionAnswered(question ?? {}, value);
    });
  }, [questions, answers]);

  const hasAnswers = useMemo(() => Object.keys(answers).length > 0, [answers]);

  const resetToFormSelection = useCallback(async () => {
    if (responseId != null) {
      void deleteResponseAsync({ responseId }).catch(() => {});
    }
    setSelectedQuestionnaireId(null);
    setAnswers({});
    setResponseId(null);
    setShowChangeFormModal(false);
  }, [responseId, deleteResponseAsync]);

  const handleSelectDifferentForm = useCallback(() => {
    if (hasAnswers) {
      setShowChangeFormModal(true);
    } else {
      resetToFormSelection();
    }
  }, [hasAnswers, resetToFormSelection]);

  if (questionnairesLoading) {
    return (
      <Center data-testid="manual-intake-dynamic-questions-loading">
        <Spinner variant="secondary" size="large" />
      </Center>
    );
  }

  const hasSelectedQuestionnaire = Boolean(selectedQuestionnaireId);
  const sectionTitle = hasSelectedQuestionnaire
    ? selectedQuestionnaireName
    : questionnaires.length > 0
      ? 'Select Form'
      : 'No forms available';

  const hasQuestions =
    hasSelectedQuestionnaire &&
    !questionsLoading &&
    !isDraftQuestionnaireResponseLoading &&
    questions.length > 0;

  const showDynamicIntakeSpinner =
    isDraftQuestionnaireResponseLoading ||
    (hasSelectedQuestionnaire && (questionsFetching || questionsLoading));

  const canSyncAttachmentsToQa = Boolean(
    (responseId ?? savedResponseId)?.trim() && evidenceUploadReportId?.trim(),
  );

  const questionnaireSelectedButEmpty =
    hasSelectedQuestionnaire &&
    !questionsLoading &&
    !questionsFetching &&
    !isDraftQuestionnaireResponseLoading &&
    questions.length === 0;

  return (
    <Container data-testid="manual-intake-dynamic-questions">
      {!canSelectQuestionnaire && (
        <InformationBannerContainer>
          <InformationBanner
            data-testid="reporter-disabled-warning"
            state={FormState.WARNING}
            title="Form selection disabled"
            description="To select a form, first choose a reporter and click ‘Save draft’."
            $fullWidth={true}
          />
        </InformationBannerContainer>
      )}
      <SectionHeaderContainer>
        {hasSelectedQuestionnaire ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={handleSelectDifferentForm}
            data-testid="manual-intake-select-different-form"
          >
            <StyledIconInButton
              size="medium"
              position="left"
              className="uil uil-angle-left"
            />
            Back
          </Button>
        ) : (
          <Spacer />
        )}
        <SectionHeader>
          <SectionTitle>{sectionTitle}</SectionTitle>
          <SectionDescription>
            {hasSelectedQuestionnaire
              ? selectedQuestionnaireDescription
              : 'Select the category you want to report under'}
          </SectionDescription>
        </SectionHeader>
        <Spacer />
      </SectionHeaderContainer>
      <Modal
        size="small"
        title="Leave unfinished form?"
        description="Are you sure you want to leave this report unfinished? Your answers will be lost and cannot be recovered."
        open={showChangeFormModal}
        setOpen={setShowChangeFormModal}
        onCancel={() => setShowChangeFormModal(false)}
        onConfirm={resetToFormSelection}
        cancelButtonText="Stay here"
        confirmButtonText="Leave form"
        data-testid="manual-intake-change-form-modal"
      />

      {!hasSelectedQuestionnaire && !isDraftQuestionnaireResponseLoading && (
        <ManualIntakeQuestionnaireSelector
          questionnaires={questionnaires}
          canSelectQuestionnaire={canSelectQuestionnaire}
          onSelect={handleQuestionnaireSelect}
        />
      )}

      {showDynamicIntakeSpinner && (
        <Spinner
          variant="secondary"
          size="medium"
          data-testid="manual-intake-dynamic-questions-spinner"
        />
      )}

      {hasQuestions && (
        <ManualIntakeQuestionsForm
          questions={questions}
          answers={answers}
          setAnswer={setAnswer}
          submitAttempted={submitAttempted}
          setSubmitAttempted={setSubmitAttempted}
          allRequiredQuestionsAnswered={allRequiredQuestionsAnswered}
          isSubmittingReport={isSubmittingReport}
          canSubmitReport={canSubmitReport}
          selectedQuestionnaireId={selectedQuestionnaireId}
          responseId={responseId}
          evidenceUploadReportId={evidenceUploadReportId}
          canSyncAttachmentsToQa={canSyncAttachmentsToQa}
          onRegisterQuestionnaireAttachment={
            handleRegisterQuestionnaireAttachment
          }
          onDeleteQuestionnaireAttachment={handleDeleteQuestionnaireAttachment}
          onSubmitReport={onSubmitReport}
          onSubmitReportFromReview={onSubmitReportFromReview}
        />
      )}

      {questionnaireSelectedButEmpty && (
        <Center data-testid="manual-intake-no-questions">
          <EmptyStateWrapper>
            <SectionDescription>
              No questions available for this questionnaire.
            </SectionDescription>
            <Button
              variant="primary"
              onClick={() => setSelectedQuestionnaireId(null)}
              data-testid="manual-intake-back-to-questionnaires"
            >
              Back to forms
            </Button>
          </EmptyStateWrapper>
        </Center>
      )}
    </Container>
  );
});
